import { z } from 'zod'
import { AssemblyConnectionSchema } from './connection'
import { RobotGroupSchema } from './robot-group'

export const Vector3Schema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
})

export const PlacedPartSchema = z.object({
  instanceId: z.string(),
  partId: z.string(),
  position: Vector3Schema,
  rotation: Vector3Schema,
})

export const AssemblySchema = z.object({
  version: z.literal(1),
  name: z.string(),
  updatedAt: z.string(),
  parts: z.array(PlacedPartSchema),
  connections: z.array(AssemblyConnectionSchema).default([]),
  robotGroups: z.array(RobotGroupSchema).default([]),
})

export type PlacedPart = z.infer<typeof PlacedPartSchema>
export type Assembly = z.infer<typeof AssemblySchema>
export type StudioMode = 'assembly' | 'coding' | 'simulation'
export type StudioEnvironment = 'flat' | 'ramp' | 'road' | 'obstacle'

export const starterAssembly: PlacedPart[] = [
  { instanceId: 'pingpong-cube-main', partId: 'part-cube-stl-step', position: { x: 0, y: 0.38, z: 0 }, rotation: { x: 0, y: 0, z: 0 } },
]

const HALF_TURN = Math.PI
const QUARTER_TURN = Math.PI / 2
export const BATTLEBOT_C103_DEFAULT_FORWARD_ANGLE = Math.PI / 180

export const battlebotMiniAssembly: PlacedPart[] = [
  {
    instanceId: 'battlebot-rear-left-cube',
    partId: 'part-cube-stl-step',
    position: { x: -0.39, y: 0.62, z: 0 },
    rotation: { x: 0, y: QUARTER_TURN, z: QUARTER_TURN },
  },
  {
    instanceId: 'battlebot-rear-right-cube',
    partId: 'part-cube-stl-step',
    position: { x: 0.39, y: 0.62, z: 0 },
    rotation: { x: 0, y: -QUARTER_TURN, z: -QUARTER_TURN },
  },
  {
    instanceId: 'battlebot-c2-13',
    partId: 'part-link-c2-c2-13',
    position: { x: 0, y: 0.62, z: 0 },
    rotation: { x: 0, y: QUARTER_TURN, z: 0 },
  },
  {
    instanceId: 'battlebot-c3-02',
    partId: 'part-link-c3-c3-02',
    position: { x: 0, y: 0.62, z: -0.409 },
    rotation: { x: QUARTER_TURN, y: 0, z: 0 },
  },
  {
    instanceId: 'battlebot-front-cube',
    partId: 'part-cube-stl-step',
    position: { x: 0, y: 0.62, z: -0.818 },
    rotation: { x: 0, y: QUARTER_TURN, z: -QUARTER_TURN },
  },
  {
    instanceId: 'battlebot-left-wheel',
    partId: 'part-link-wl-wl-04',
    position: { x: -0.762, y: 0.62, z: -0.013 },
    rotation: { x: 0, y: HALF_TURN, z: 0 },
  },
  {
    instanceId: 'battlebot-right-wheel',
    partId: 'part-link-wl-wl-04',
    position: { x: 0.762, y: 0.62, z: -0.013 },
    rotation: { x: 0, y: 0, z: 0 },
  },
  {
    instanceId: 'battlebot-c1-03',
    partId: 'part-link-c1-c1-03',
    // Start one degree forward around the motor/cross hinge. The position is
    // rotated around that hinge too, so both connector axes stay seated.
    position: { x: 0.008, y: 0.4863506487746616, z: -1.3026073008183103 },
    rotation: {
      x: QUARTER_TURN - BATTLEBOT_C103_DEFAULT_FORWARD_ANGLE,
      y: -HALF_TURN,
      z: -QUARTER_TURN,
    },
  },
  {
    instanceId: 'battlebot-c1-19',
    partId: 'part-link-c1-c1-19',
    position: { x: 0, y: 0.184, z: -0.818 },
    rotation: { x: 0, y: 0, z: 0 },
  },
]

const MD01_INPUT_TO_OUTPUT_SCENE_DISTANCE = 0.294

/**
 * The regular Battlebot uses one MD-01 acceleration gearbox between each rear
 * cube motor and WL-04 wheel. Battlebot Mini is the direct-drive variant above.
 */
export const battlebotAssembly: PlacedPart[] = battlebotMiniAssembly.flatMap((part) => {
  if (part.instanceId === 'battlebot-left-wheel') {
    return [
      {
        instanceId: 'battlebot-left-md-01',
        partId: 'part-link-md-md-01',
        position: { x: -0.67003, y: 0.62, z: -0.013 },
        rotation: { x: 0, y: 0, z: QUARTER_TURN },
      },
      {
        ...part,
        position: {
          ...part.position,
          x: part.position.x - MD01_INPUT_TO_OUTPUT_SCENE_DISTANCE,
        },
      },
    ]
  }
  if (part.instanceId === 'battlebot-right-wheel') {
    return [
      {
        instanceId: 'battlebot-right-md-01',
        partId: 'part-link-md-md-01',
        position: { x: 0.67003, y: 0.62, z: -0.013 },
        rotation: { x: 0, y: 0, z: -QUARTER_TURN },
      },
      {
        ...part,
        position: {
          ...part.position,
          x: part.position.x + MD01_INPUT_TO_OUTPUT_SCENE_DISTANCE,
        },
      },
    ]
  }
  return [part]
})
